import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  ButtonBase,
} from "@mui/material";
import { LowValueCustomerData } from "./types";
import { DashboardSectionDivider, useActivateDashboardSection } from "../DashboardSectionDivider";

interface LowValueCustomerProps {
  data: LowValueCustomerData;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

const headerCellSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "11px",
  fontWeight: 600,
  color: "#6b7280",
  backgroundColor: "#f8fafc",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "none",
  py: 1,
  px: 1.35,
  whiteSpace: "nowrap" as const,
  letterSpacing: "0.01em",
};

const bodyCellSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  color: "#111827",
  borderBottom: "1px solid #f1f5f9",
  borderRight: "none",
  py: 1.15,
  px: 1.35,
};

/** Vertical column markings between metrics */
const colMark = {
  borderRight: "1px solid #d4c8c9",
};

const SoftTag: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 0.85,
      py: 0.25,
      borderRadius: "999px",
      backgroundColor: "#f1f5f9",
      border: "1px solid #e2e8f0",
      fontFamily: "'Poppins', sans-serif",
      fontSize: "10.5px",
      fontWeight: 600,
      color: "#475569",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

export const LowValueCustomer: React.FC<LowValueCustomerProps> = ({ data }) => {
  const { activateSection } = useActivateDashboardSection();
  const hasHighValueTab = Boolean(data.highValueCustomers?.length);
  const [activeTab, setActiveTab] = useState<"low" | "high">("low");

  const rows = useMemo(
    () =>
      (activeTab === "high" && data.highValueCustomers
        ? data.highValueCustomers
        : data.customers
      ).slice(0, 5),
    [activeTab, data.customers, data.highValueCustomers],
  );

  const pillButtonSx = (selected: boolean) => ({
    px: 1.1,
    py: 0.35,
    borderRadius: "999px",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "10.5px",
    lineHeight: 1.2,
    fontWeight: selected ? 600 : 500,
    color: selected ? "#ffffff" : "#555555",
    backgroundColor: selected ? "#d2363a" : "transparent",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap" as const,
    "&:hover": {
      backgroundColor: selected ? "#b92d31" : "#e5e7eb",
    },
  });

  return (
    <Box sx={{ mt: 2.75 }} onClickCapture={() => activateSection("low-value")}>
      <DashboardSectionDivider sectionId="low-value" />
      <Box
        sx={{
          mb: 1.25,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.25,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {hasHighValueTab ? "Customer Value" : "Low Value Customer"}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "11px",
              color: "#6b7280",
              mt: 0.2,
            }}
          >
            {activeTab === "high"
              ? "Top 5 customers with the highest total value"
              : "Top 5 customers with the lowest total value"}
          </Typography>
        </Box>
        {hasHighValueTab ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              border: "1px solid #e5e7eb",
              borderRadius: "999px",
              p: 0.35,
              backgroundColor: "#ffffff",
            }}
          >
            <ButtonBase
              sx={pillButtonSx(activeTab === "low")}
              onClick={() => setActiveTab("low")}
            >
              Low Value Customer
            </ButtonBase>
            <ButtonBase
              sx={pillButtonSx(activeTab === "high")}
              onClick={() => setActiveTab("high")}
            >
              High Value Customer
            </ButtonBase>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          borderRadius: "12px",
          border: "1px solid #d4c8c9",
          backgroundColor: "#ffffff",
          overflow: "hidden",
          boxShadow:
            "0 2px 4px rgba(42, 18, 20, 0.08), 0 10px 24px rgba(42, 18, 20, 0.12), 0 1px 0 rgba(255,255,255,0.85) inset",
          transition: "box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.25s ease",
          "@keyframes dashboardPanelIn": {
            from: {
              opacity: 0,
              transform: "translateY(10px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
          animation: "dashboardPanelIn 560ms cubic-bezier(0.22, 1, 0.36, 1) both",
          "&:hover": {
            borderColor: "#c9bbbc",
            boxShadow:
              "0 4px 10px rgba(42, 18, 20, 0.12), 0 14px 32px rgba(90, 24, 28, 0.16), 0 1px 0 rgba(255,255,255,0.9) inset",
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ ...headerCellSx, ...colMark }}>
                  Customer Name
                </TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellSx, ...colMark }}>
                  Branch
                </TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellSx, ...colMark }}>
                  Industry
                </TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellSx, ...colMark }}>
                  Phone Number
                </TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellSx, ...colMark }}>
                  Company Email
                </TableCell>
                <TableCell
                  align="center"
                  colSpan={2}
                  sx={{
                    ...headerCellSx,
                    color: "#111827",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  Total Value
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center" sx={{ ...headerCellSx, ...colMark }}>
                  Count
                </TableCell>
                <TableCell align="right" sx={headerCellSx}>
                  Total Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafbfc",
                    transition: "background-color 0.15s ease",
                    "&:hover": { backgroundColor: "#fff5f5" },
                  }}
                >
                  <TableCell sx={{ ...bodyCellSx, ...colMark }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.85,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          backgroundColor: "#fff1f1",
                          color: "#d2363a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: "10px",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(row.customerName)}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.customerName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, ...colMark }}>
                    <SoftTag label={row.branch} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, ...colMark }}>
                    <SoftTag label={row.industry} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, ...colMark, color: "#374151" }}>
                    {row.phoneNumber}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, ...colMark, color: "#374151" }}>
                    {row.companyEmail}
                  </TableCell>
                  <TableCell align="center" sx={{ ...bodyCellSx, ...colMark }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 40,
                        px: 0.9,
                        py: 0.3,
                        borderRadius: "8px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        color: "#334155",
                      }}
                    >
                      {row.orderCount}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={bodyCellSx}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1,
                        py: 0.35,
                        borderRadius: "8px",
                        backgroundColor: "#fff1f1",
                        border: "1px solid #fecaca",
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        color: "#d2363a",
                      }}
                    >
                      {formatCurrency(row.totalOrderValue)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
